<template>
  <v-text-field
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :label="label"
    :aria-label="ariaLabel"
    :prepend-inner-icon="prependInnerIcon"
    :prepend-icon="prependIcon"
    :append-inner-icon="appendInnerIcon"
    :append-icon="appendIcon"
    :required="required"
    :error-messages="errorMessages"
    :disabled="disabled"
    :readonly="readonly"
    :placeholder="placeholder"
    :persistent-placeholder="persistencePlaceholder"
    :type="type"
    :variant="variant"
    :density="density"
    :bg-color="bgColor"
    :color="color"
    :rules="computedRules"
    :hint="hint"
    :persistent-hint="persistentHint"
    :clearable="clearable"
    :counter="counter"
    :max="max"
    :min="min"
    :step="step"
    :prefix="prefix"
    :suffix="suffix"
    :loading="loading"
    :autofocus="autofocus"
    :autocomplete="autocomplete"
    :maxlength="maxlength"
    :minlength="minlength"
    :pattern="pattern"
    :spellcheck="spellcheck"
    :single-line="singleLine"
    v-bind="$attrs"
  />
</template>

<script setup>
import { computed } from 'vue'

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: {
    type: String,
    required: true
  },
  prependInnerIcon: {
    type: String,
    default: null
  },
  prependIcon: {
    type: String,
    default: null
  },
  appendInnerIcon: {
    type: String,
    default: null
  },
  appendIcon: {
    type: String,
    default: null
  },
  required: {
    type: Boolean,
    default: false
  },
  errorMessages: {
    type: [String, Array],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: null
  },
  persistencePlaceholder: {
    type: Boolean,
    default: true
  },
  type: {
    type: String,
    default: 'text'
  },
  variant: {
    type: String,
    default: 'filled'
  },
  density: {
    type: String,
    default: 'default'
  },
  bgColor: {
    type: String,
    default: null
  },
  color: {
    type: String,
    default: null
  },
  rules: {
    type: Array,
    default: () => []
  },
  hint: {
    type: String,
    default: null
  },
  persistentHint: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  counter: {
    type: [Boolean, Number, String],
    default: false
  },
  max: {
    type: [Number, String],
    default: null
  },
  min: {
    type: [Number, String],
    default: null
  },
  step: {
    type: [Number, String],
    default: null
  },
  prefix: {
    type: String,
    default: null
  },
  suffix: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: null
  },
  maxlength: {
    type: [Number, String],
    default: null
  },
  minlength: {
    type: [Number, String],
    default: null
  },
  pattern: {
    type: String,
    default: null
  },
  spellcheck: {
    type: Boolean,
    default: true
  },
  singleLine: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:modelValue'])

// Auto-generate aria-label from label (priority) or placeholder (fallback)
const ariaLabel = computed(() => {
  return props.label || props.placeholder || ''
})

// Automatically create validation rules when required is true
const computedRules = computed(() => {
  const allRules = [...(props.rules || [])]

  if (props.required) {
    const requiredRule = (value) => {
      // Handle different types of empty values
      if (value === null || value === undefined) return `${props.label} is required`
      if (typeof value === 'string' && value.trim() === '') return `${props.label} is required`
      if (props.type === 'number') {
        // For number inputs, check if value is empty string or NaN
        if (value === '' || isNaN(Number(value))) return `${props.label} is required`
      }
      return true
    }
    allRules.unshift(requiredRule)
  }

  return allRules
})
</script>
